// ---------------------------------------------------------------------------
// Leaderboard storage adapter.
//
// Two backends behind one interface so the UI never changes:
//   - localStorage (default): a per-device high-score table. Works today on any
//     host with zero infrastructure.
//   - API: a Cloudflare Pages Function (`/api/leaderboard`) backed by D1, for a
//     real global board shared across all players.
//
// To go global: deploy the Pages Function + D1 (see LEADERBOARD-SETUP.md), then
// flip USE_API to true. Nothing else in the app needs to change.
// ---------------------------------------------------------------------------

const USE_API = true; // global D1 board via /api/leaderboard (no local board)
const API_URL = "/api/leaderboard";
const STORAGE_KEY = "drugbiz_leaderboard";

export const LEADERBOARD_SIZE = 10;

const sortAndTrim = (scores) =>
  [...scores].sort((a, b) => b.netWorth - a.netWorth).slice(0, LEADERBOARD_SIZE);

// Returns the current top scores, highest net worth first.
export async function getTopScores() {
  if (USE_API) {
    try {
      const res = await fetch(API_URL);
      if (!res.ok) throw new Error(`Leaderboard fetch failed: ${res.status}`);
      return await res.json();
    } catch (err) {
      console.error(err);
      return [];
    }
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? sortAndTrim(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
}

// Submits a score and returns the updated top scores.
// `entry` = { initials, netWorth, day, date, ts }
export async function submitScore(entry) {
  if (USE_API) {
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(entry),
      });
      if (!res.ok) throw new Error(`Score submit failed: ${res.status}`);
      return await res.json();
    } catch (err) {
      console.error(err);
      return getTopScores();
    }
  }

  const current = await getTopScores();
  const updated = sortAndTrim([...current, entry]);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.error(err);
  }
  return updated;
}

// True if `netWorth` earns a spot on the board (board not full, or beats last).
export function qualifies(netWorth, scores) {
  if (scores.length < LEADERBOARD_SIZE) return true;
  return netWorth > scores[scores.length - 1].netWorth;
}
